import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeftRegular, DocumentLandscapeSplitRegular, DocumentPdfRegular, DocumentRegular } from "@fluentui/react-icons";
import { useOfficeContext } from "../../../../../shared/hooks/useOfficeContext";
import { logEvent, logOfficeError } from "../../../../../telemetry";
import { useDocuparserDocumentDetail } from "../hooks/useDocuparserDocumentDetail";
import { usePdfManager } from "../hooks/usePdfManager";
import { useDocxPreview } from "../hooks/useDocxPreview";
import type { DocumentDetail } from "../../../services/docuparserApi";
import { fetchFileAsBase64 } from "../../../services/presignedUpload";
import { DOCUMENT_STATUS, type TabType } from "../../../constants";
import { DetailMetadata } from "./DetailMetadata";
import { PdfViewer } from "./PdfViewer";
import { CompareView } from "./CompareView";
import styles from "./DetailView.module.css";

function getIsOpenButtonEnabled(doc: DocumentDetail | null, isOpeningInWord: boolean): boolean {
  if (!doc) return false;
  const isCompleted = doc.status?.toLowerCase() === DOCUMENT_STATUS.COMPLETED;
  return isCompleted && !!doc.outputPresignedUrl && !isOpeningInWord;
}

async function checkIfDocumentHasContent(): Promise<boolean> {
  try {
    return await Word.run(async (context) => {
      const range = context.document.body.getRange(Word.RangeLocation.whole);
      range.load("text");
      await context.sync();
      return range.text.trim().length > 0;
    });
  } catch {
    return false;
  }
}

async function clearDocumentContent(): Promise<void> {
  await Word.run(async (context) => {
    context.document.body.clear();
    await context.sync();
  });
}

async function insertDocumentIntoWord(base64Content: string): Promise<void> {
  await Word.run(async (context) => {
    context.document.insertFileFromBase64(base64Content, Word.InsertLocation.replace);
    await context.sync();
    const startRange = context.document.body.getRange(Word.RangeLocation.start);
    startRange.select(Word.SelectionMode.start);
    await context.sync();
  });
}

interface DocumentDetailViewProps {
  readonly documentId: number;
  readonly onBack: () => void;
}

export function DocumentDetailView({ documentId, onBack }: Readonly<DocumentDetailViewProps>) {
  const { isWord } = useOfficeContext();
  const [activeTab, setActiveTab] = useState<TabType>("document");
  const [isOpeningInWord, setIsOpeningInWord] = useState(false);
  const [isWordOperationActive, setIsWordOperationActive] = useState(false);
  const [showConfirmReplace, setShowConfirmReplace] = useState(false);
  const pdfShouldLoadRef = useRef(false);
  const hasInitialPdfLoadedRef = useRef(false);
  const docxShouldLoadRef = useRef(false);
  const hasInitialDocxLoadedRef = useRef(false);
  const tabSwitchPendingRef = useRef(false);

  const { doc, isLoading, error } = useDocuparserDocumentDetail(documentId);
  const {
    totalPages,
    zoomLevel,
    pdfError,
    isLoadingPdf,
    isPdfLoaded,
    canvasRefs,
    zoomIn,
    zoomOut,
    resetZoom,
    setZoom,
    renderAllPages,
    loadPdf,
    resetPdf,
    pdfDoc,
  } = usePdfManager();
  const {
    html: docxHtml,
    isLoadingDocx,
    docxError,
    isDocxLoaded,
    loadDocx,
    resetDocx,
    zoomLevel: docxZoomLevel,
    zoomIn: docxZoomIn,
    zoomOut: docxZoomOut,
    resetZoom: docxResetZoom,
    setZoom: docxSetZoom,
  } = useDocxPreview();

  useEffect(() => {
    const shouldLoad =
      (activeTab === "pdf" || activeTab === "compare") &&
      doc?.inputPresignedUrl &&
      !pdfDoc &&
      !isLoadingPdf &&
      pdfShouldLoadRef.current &&
      !isWordOperationActive;
    if (shouldLoad) {
      // Consume the request before dispatching it. `isLoadingPdf` is a
      // dependency, so a FAILED load re-fires this effect the moment it flips
      // back to false — and every other condition (ref still true, pdfDoc
      // still null) is still satisfied, so the load would be dispatched a
      // second time. Retry re-arms the ref explicitly.
      pdfShouldLoadRef.current = false;
      loadPdf(doc.inputPresignedUrl);
    }
  }, [activeTab, doc?.inputPresignedUrl, pdfDoc, isLoadingPdf, isWordOperationActive, loadPdf]);

  useEffect(() => {
    if (pdfDoc && totalPages > 0 && (activeTab === "pdf" || activeTab === "compare") && !isWordOperationActive) {
      renderAllPages();
    }
  }, [pdfDoc, totalPages, zoomLevel, activeTab, isWordOperationActive, renderAllPages]);

  useEffect(() => {
    const shouldLoad =
      activeTab === "compare" &&
      doc?.outputPresignedUrl &&
      !isDocxLoaded &&
      !isLoadingDocx &&
      docxShouldLoadRef.current &&
      !isWordOperationActive;
    if (shouldLoad) {
      // Same one-shot dispatch as the PDF effect above — see its comment.
      docxShouldLoadRef.current = false;
      loadDocx(doc.outputPresignedUrl);
    }
  }, [activeTab, doc?.outputPresignedUrl, isDocxLoaded, isLoadingDocx, isWordOperationActive, loadDocx]);

  const handleRetryPdf = useCallback(() => {
    if (!doc?.inputPresignedUrl || isWordOperationActive) return;
    resetPdf();
    // Deliberately does NOT arm pdfShouldLoadRef: this dispatches the load
    // itself, and the ref is what the auto-load effect consumes. Arming it
    // here as well would leave a pending request the effect picks up the
    // moment isLoadingPdf flips back to false, loading the same URL twice.
    hasInitialPdfLoadedRef.current = false;
    loadPdf(doc.inputPresignedUrl);
  }, [doc?.inputPresignedUrl, isWordOperationActive, resetPdf, loadPdf]);

  const handleRetryDocx = useCallback(() => {
    if (!doc?.outputPresignedUrl || isWordOperationActive) return;
    resetDocx();
    // Same reason as handleRetryPdf — see its comment.
    hasInitialDocxLoadedRef.current = false;
    loadDocx(doc.outputPresignedUrl);
  }, [doc?.outputPresignedUrl, isWordOperationActive, resetDocx, loadDocx]);

  const handleTabChange = useCallback(
    (tab: TabType) => {
      if (isWordOperationActive || tabSwitchPendingRef.current) return;
      tabSwitchPendingRef.current = true;
      const needsPdf = tab === "pdf" || tab === "compare";
      if (needsPdf && doc?.inputPresignedUrl && !hasInitialPdfLoadedRef.current) {
        pdfShouldLoadRef.current = true;
        hasInitialPdfLoadedRef.current = true;
      }
      if (tab === "compare" && doc?.outputPresignedUrl && !hasInitialDocxLoadedRef.current) {
        docxShouldLoadRef.current = true;
        hasInitialDocxLoadedRef.current = true;
      }
      setActiveTab(tab);
      setTimeout(() => {
        tabSwitchPendingRef.current = false;
      }, 150);
    },
    [isWordOperationActive, doc?.inputPresignedUrl, doc?.outputPresignedUrl],
  );

  const performInsert = useCallback(
    async (base64: string, mode: "replace" | "insert") => {
      if (mode === "replace") setShowConfirmReplace(false);
      setIsWordOperationActive(true);
      setIsOpeningInWord(true);
      try {
        if (mode === "replace") await clearDocumentContent();
        await insertDocumentIntoWord(base64);
        logEvent("connector.download", "usage", { connectorSystemName: "docuparser", fileType: "docx" });
      } catch (err) {
        logOfficeError(err, `docuparser-word-insert-${mode}`);
      } finally {
        setIsWordOperationActive(false);
        setIsOpeningInWord(false);
      }
    },
    [],
  );

  const handleOpen = useCallback(async () => {
    if (!doc?.outputPresignedUrl || !isWord) return;
    try {
      const hasContent = await checkIfDocumentHasContent();
      if (hasContent) {
        setShowConfirmReplace(true);
      } else {
        const base64Content = await fetchFileAsBase64(doc.outputPresignedUrl);
        await performInsert(base64Content, "insert");
      }
    } catch (err) {
      logOfficeError(err, "docuparser-word-open-flow");
    }
  }, [doc?.outputPresignedUrl, isWord, performInsert]);

  const handleConfirmYes = useCallback(async () => {
    if (!doc?.outputPresignedUrl) return;
    try {
      const base64 = await fetchFileAsBase64(doc.outputPresignedUrl);
      await performInsert(base64, "replace");
    } catch (err) {
      logOfficeError(err, "docuparser-word-replace");
    }
  }, [doc?.outputPresignedUrl, performInsert]);

  const handleConfirmNo = useCallback(() => setShowConfirmReplace(false), []);

  const isOpenButtonEnabled = getIsOpenButtonEnabled(doc, isOpeningInWord) && isWord;
  const isPdfTabDisabled = !doc?.inputPresignedUrl || doc?.status?.toLowerCase() === DOCUMENT_STATUS.FAILED;
  const isCompareTabDisabled =
    !doc?.inputPresignedUrl || !doc?.outputPresignedUrl || doc?.status?.toLowerCase() !== DOCUMENT_STATUS.COMPLETED;

  return (
    <div className={styles.detailContainer}>
      <div className={styles.detailHeader}>
        <button type="button" className={styles.backBtn} onClick={onBack} aria-label="Go back">
          <ArrowLeftRegular fontSize={20} />
        </button>
        <div className={styles.tabContainer}>
          <button
            type="button"
            className={`${styles.tabButton} ${activeTab === "document" ? styles.tabButtonActive : ""}`}
            onClick={() => handleTabChange("document")}
            aria-label="Document tab"
          >
            <DocumentRegular />
            <span>Document</span>
          </button>
          <button
            type="button"
            className={`${styles.tabButton} ${activeTab === "pdf" ? styles.tabButtonActive : ""}`}
            onClick={() => handleTabChange("pdf")}
            disabled={isPdfTabDisabled}
            aria-label="Original PDF tab"
            title={isPdfTabDisabled ? "Original PDF unavailable" : undefined}
          >
            <DocumentPdfRegular />
            <span>Original PDF</span>
          </button>
          <button
            type="button"
            className={`${styles.tabButton} ${activeTab === "compare" ? styles.tabButtonActive : ""}`}
            onClick={() => handleTabChange("compare")}
            disabled={isCompareTabDisabled}
            aria-label="Compare tab"
            title={isCompareTabDisabled ? "Compare unavailable until conversion completes" : undefined}
          >
            <DocumentLandscapeSplitRegular />
            <span>Compare</span>
          </button>
        </div>
      </div>

      {showConfirmReplace && (
        <div className={styles.confirmationOverlay}>
          <div className={styles.confirmationDialog}>
            <h3>Replace Existing Content?</h3>
            <p>The current Word document already contains content. Do you want to replace it with the selected document?</p>
            <div className={styles.confirmationButtons}>
              <button type="button" className={styles.btnNo} onClick={handleConfirmNo}>
                No
              </button>
              <button type="button" className={styles.btn} onClick={handleConfirmYes}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === "document" && (
        <DetailMetadata
          doc={doc}
          isLoading={isLoading}
          error={error}
          isOpeningInWord={isOpeningInWord}
          isOpenButtonEnabled={isOpenButtonEnabled}
          onOpen={handleOpen}
          onBack={onBack}
        />
      )}

      {activeTab === "pdf" && (
        <PdfViewer
          isWordOperationActive={isWordOperationActive}
          isLoadingPdf={isLoadingPdf}
          pdfError={pdfError}
          pdfLoaded={isPdfLoaded}
          totalPages={totalPages}
          zoomLevel={zoomLevel}
          canvasRefs={canvasRefs}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          onResetZoom={resetZoom}
          onZoomChange={setZoom}
          onRetry={handleRetryPdf}
        />
      )}

      {activeTab === "compare" && (
        <CompareView
          isWordOperationActive={isWordOperationActive}
          isLoadingPdf={isLoadingPdf}
          pdfError={pdfError}
          pdfLoaded={isPdfLoaded}
          totalPages={totalPages}
          zoomLevel={zoomLevel}
          canvasRefs={canvasRefs}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          onResetZoom={resetZoom}
          onZoomChange={setZoom}
          onRetryPdf={handleRetryPdf}
          isLoadingDocx={isLoadingDocx}
          docxError={docxError}
          docxHtml={docxHtml}
          isDocxLoaded={isDocxLoaded}
          onRetryDocx={handleRetryDocx}
          docxZoomLevel={docxZoomLevel}
          onDocxZoomIn={docxZoomIn}
          onDocxZoomOut={docxZoomOut}
          onDocxResetZoom={docxResetZoom}
          onDocxZoomChange={docxSetZoom}
        />
      )}
    </div>
  );
}
